// Generado desde design-system/components/motion/motion-utils.ts — no editar; edita el .ts/.tsx y corre build-ds.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
/*
 * Data Bunker · utilidades de movimiento (sin dependencias).
 * Espejo JS de los tokens de tokens.css (--db-ease-*, --db-dur-*), resorte real
 * como curva CSS linear(), física de resortes para rAF y detección de
 * prefers-reduced-motion. Todo es seguro en SSR / Node (no toca window al importar).
 */
export const EASE = {
    out: 'cubic-bezier(.32, .72, 0, 1)', // --db-ease-out · cierres, deslizamientos
    expo: 'cubic-bezier(.16, 1, .3, 1)', // --db-ease-expo · giros largos (flip), conteos
    spring: 'cubic-bezier(.34, 1.3, .5, 1)', // --db-ease-spring · respaldo del resorte real
};
/** Duraciones en ms (espejo de --db-dur-*). */
export const DUR = { fast: 140, base: 350, close: 620, open: 850, long: 1100 };
/** Muestrea un resorte amortiguado (z amortiguamiento, w frecuencia) como curva CSS linear(). */
export function springLinear({ z = 0.74, w = 13, n = 48, t = 0.85 } = {}) {
    const wd = w * Math.sqrt(1 - z * z);
    const pts = [];
    for (let i = 0; i <= n; i++) {
        const s = (i / n) * t;
        const v = 1 - Math.exp(-z * w * s) * (Math.cos(wd * s) + ((z * w) / wd) * Math.sin(wd * s));
        pts.push(i === n ? 1 : +v.toFixed(4));
    }
    return `linear(${pts.join(', ')})`;
}
/** Resorte real (linear()) precalculado; con respaldo cubic-bezier si el navegador no soporta linear(). */
export const SPRING_LINEAR = springLinear();
let springSupport;
/** Curva de resorte para WAAPI/estilos en línea: linear() real si hay soporte, si no el respaldo. */
export function springEasing() {
    if (springSupport === undefined) {
        springSupport =
            typeof CSS !== 'undefined' && !!CSS.supports && CSS.supports('transition-timing-function', 'linear(0, 1)');
    }
    return springSupport ? SPRING_LINEAR : EASE.spring;
}
/** Paso de integración de un resorte: s = { x, v }; k rigidez; c amortiguamiento; h paso (s). */
export function stepSpring(s, target, k, c, h) {
    s.v += (-k * (s.x - target) - c * s.v) * h;
    s.x += s.v * h;
}
/** ¿El usuario pidió menos movimiento? (lectura puntual, segura en SSR) */
export function prefersReducedMotion() {
    return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
/** Hook: true si prefers-reduced-motion: reduce. Se actualiza si la preferencia cambia. */
export function useReducedMotion() {
    const [reduce, setReduce] = useState(prefersReducedMotion);
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia)
            return undefined;
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const on = () => setReduce(mq.matches);
        on();
        mq.addEventListener?.('change', on);
        return () => mq.removeEventListener?.('change', on);
    }, []);
    return reduce;
}
/**
 * Inyecta una hoja de estilo una sola vez (idempotente; reemplaza el contenido si cambió).
 * Los componentes del sistema la usan para keyframes y @property que Tailwind no expresa.
 */
export function injectStyles(id, css) {
    if (typeof document === 'undefined')
        return;
    let el = document.getElementById(id);
    if (el) {
        if (el.textContent !== css)
            el.textContent = css;
        return;
    }
    el = document.createElement('style');
    el.id = id;
    el.setAttribute('data-db', 'motion');
    el.textContent = css;
    document.head.appendChild(el);
}
/** Bucle rAF con paso fijo (1/240 s) que se detiene cuando `tick` devuelve false. Devuelve cancel(). */
export function runLoop(tick) {
    if (typeof window === 'undefined')
        return () => { };
    let raf = 0;
    let last = performance.now();
    let acc = 0;
    const h = 1 / 240;
    const frame = (t) => {
        const dt = Math.min(0.05, (t - last) / 1000 || 0.016);
        last = t;
        acc += dt;
        let alive = true;
        while (acc >= h) {
            alive = tick(h, t) !== false;
            acc -= h;
            if (!alive)
                break;
        }
        if (alive)
            raf = requestAnimationFrame(frame);
        else
            raf = 0;
    };
    raf = requestAnimationFrame(frame);
    return () => {
        if (raf)
            cancelAnimationFrame(raf);
        raf = 0;
    };
}
/** useLayoutEffect en el navegador, useEffect en SSR (evita el aviso de React en servidor). */
export const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
/**
 * FLIP para listas: anima a los hijos con [data-flip-key] desde su posición anterior.
 * Llama al hook con `deps` que cambian cuando cambia el orden o el número de hijos.
 */
export function useFlipList(containerRef, deps, { duration = DUR.open, easing } = {}) {
    const rects = useRef(new Map());
    useIsoLayoutEffect(() => {
        const el = containerRef.current;
        if (!el)
            return;
        const reduce = prefersReducedMotion();
        const next = new Map();
        el.querySelectorAll('[data-flip-key]').forEach((node) => {
            // el selector garantiza el atributo: getAttribute nunca da null aquí
            const key = node.getAttribute('data-flip-key') ?? '';
            const r = node.getBoundingClientRect();
            next.set(key, r);
            const prev = rects.current.get(key);
            if (!prev || reduce || !node.animate)
                return;
            const dx = prev.left - r.left;
            const dy = prev.top - r.top;
            if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5)
                return;
            node.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }], {
                duration,
                easing: easing || springEasing(),
            });
        });
        rects.current = next;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}
/** Formateadores es-MX reutilizables. `d` = decimales. */
export const fmt = {
    number: (v, d = 0) => v.toLocaleString('es-MX', { minimumFractionDigits: d, maximumFractionDigits: d }),
    currency: (v, d = 2) => v.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: d, maximumFractionDigits: d }),
    percent: (v, d = 1) => `${v.toLocaleString('es-MX', { minimumFractionDigits: d, maximumFractionDigits: d })} %`,
    compact: (v) => v.toLocaleString('es-MX', { notation: 'compact', maximumFractionDigits: 1 }),
};
